import React from 'react';

interface CardProps {
    children: React.ReactNode;
    className?: string;
    /** Id of the card's title; renders the card as a named section (a region landmark). */
    ariaLabelledBy?: string;
}

export const Card: React.FC<CardProps> = ({ children, className = '', ariaLabelledBy }) => {
    const Element = ariaLabelledBy ? 'section' : 'div';
    return (
        <Element
            aria-labelledby={ariaLabelledBy}
            className={`bg-mp-surface text-mp-foreground rounded-lg border border-mp-border shadow-sm ${className}`}
        >
            {children}
        </Element>
    );
};

interface CardHeaderProps {
    children: React.ReactNode;
    className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
    return <div className={`px-6 py-4 border-b border-mp-border ${className}`}>{children}</div>;
};

interface CardTitleProps {
    children: React.ReactNode;
    className?: string;
    /** h2 by default: a card is a top-level section under the page h1. Use h3 only when nested in another section. */
    as?: 'h2' | 'h3' | 'h4';
    /** `sm` is the small uppercase label style for compact side-column cards. */
    size?: 'md' | 'sm';
    id?: string;
}

const TITLE_SIZE_CLASSES = {
    md: 'text-lg font-semibold',
    sm: 'text-xs font-medium uppercase tracking-wide text-mp-muted',
};

export const CardTitle: React.FC<CardTitleProps> = ({ children, className = '', as: Heading = 'h2', size = 'md', id }) => {
    return (
        <Heading id={id} className={`${TITLE_SIZE_CLASSES[size]} ${className}`}>
            {children}
        </Heading>
    );
};

interface CardMediaProps {
    children: React.ReactNode;
    className?: string;
}

/** The card's first child: clips its image to the card's top corners and centres it. */
export const CardMedia: React.FC<CardMediaProps> = ({ children, className = '' }) => {
    return (
        <figure className={`flex items-center justify-center overflow-hidden rounded-t-lg ${className}`}>
            {children}
        </figure>
    );
};

interface CardContentProps {
    children: React.ReactNode;
    className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className = '' }) => {
    return <div className={`px-6 py-4 ${className}`}>{children}</div>;
};

interface CardFooterProps {
    children: React.ReactNode;
    className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => {
    return (
        <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t border-mp-border rounded-b-lg ${className}`}>
            {children}
        </div>
    );
};
